'use client';

import {
  POPULARITY_SORT,
  RELEVANCE_SORT,
  getSortCriteriaForSearchHub,
} from '@components/Search/hooks/useSortingController';
import {
  buildContext,
  buildCriterionExpression,
  buildSort,
} from '@coveo/atomic-react';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { useController } from '@lib/hooks/useController';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEngine } from '../EngineContext';

export const useSearchV2Sort = () => {
  const eng = useEngine();
  const isListingPage = eng.state.searchHub === CoveoSearchHub.LISTING;
  const defaultSort = isListingPage ? POPULARITY_SORT : RELEVANCE_SORT;
  const sortCriteria = getSortCriteriaForSearchHub(eng.state.searchHub);

  const sortController = useMemo(
    () =>
      buildSort(eng, {
        initialState: { criterion: defaultSort.criterion },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const coveoContext = useMemo(() => buildContext(eng), [eng]);
  const sortState = useController(sortController);

  const [currentSortValue, setCurrentSortValue] = useState(defaultSort.value);

  // Set popularity context on mount for listing pages
  useEffect(() => {
    if (isListingPage) {
      // @ts-ignore — Coveo context accepts boolean despite TS typing
      coveoContext.add('popularity', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync sort value when URL restores a non-relevancy sort.
  // Falls back to defaultSort for unknown criteria (stale/hand-edited URLs)
  // so the popularity context never drifts out of sync with the displayed sort.
  useEffect(() => {
    if (sortState.sortCriteria !== 'relevancy') {
      const matched = sortCriteria.find(
        (opt) =>
          buildCriterionExpression(opt.criterion) === sortState.sortCriteria
      );
      const resolvedValue = matched?.value ?? defaultSort.value;
      setCurrentSortValue(resolvedValue);
      // @ts-ignore — Coveo context accepts boolean despite TS typing
      coveoContext.add('popularity', resolvedValue === 'popularity');
    }
  }, [sortState.sortCriteria, coveoContext, sortCriteria, defaultSort.value]);

  const handleSort = useCallback(
    (value: string) => {
      const option = sortCriteria.find((opt) => opt.value === value);
      if (!option) return;
      if (value === 'popularity') {
        // @ts-ignore — Coveo context accepts boolean despite TS typing
        coveoContext.add('popularity', true);
      } else {
        // @ts-ignore — Coveo context accepts boolean despite TS typing
        coveoContext.add('popularity', false);
      }
      sortController.sortBy(option.criterion);
      setCurrentSortValue(value);
    },
    [sortController, coveoContext, sortCriteria]
  );

  return { currentSortValue, sortCriteria, handleSort };
};
